import type { ColDef, NestedFieldPaths, ValueFormatterParams } from 'ag-grid-enterprise';
import { useTranslation } from 'react-i18next';
import type { InvestmentStrategy } from '../api/generated/client';
import { BalanceSheetTableActionCell } from '../components/wealth/balanceSheet/BalanceSheetTableActionCell';
import { BalanceSheetTableInstitutionCell } from '../components/wealth/balanceSheet/BalanceSheetTableInstitutionCell';
import { BalanceSheetTableRefinancingCell } from '../components/wealth/balanceSheet/BalanceSheetTableRefinancingCell';
import { TruncatedCellText } from '../components/wealth/balanceSheet/TruncatedCellText';
import { INCOME_EXPENSE_INPUT_DATE_FORMAT, investmentStrategyMap } from '../config/constants';
import type { BalanceSheetTableData } from '../interfaces/balance-sheet-table';
import { getDateString } from '../utils/date-utils';
import { formatAmount } from '../utils/renderUtils';

const amountFormatter = ({ value, data }: ValueFormatterParams<BalanceSheetTableData, number>) => {
    if (!value) return value === 0 ? '0' : '';

    return data?.isBankable ? formatAmount(value, false, '', true) : formatAmount(value);
};

export function useBalanceSheetTableColDefs(
    tableType: 'assets' | 'liabilities' | 'incomeAndExpenses',
    referenceCurrency: string,
    showAll?: boolean,
): ColDef<BalanceSheetTableData>[] {
    const { t } = useTranslation();

    if (tableType === 'assets' || tableType === 'liabilities') {
        return [
            {
                field: 'name',
                headerName: t('wealthItemName'),
                sortable: true,
                width: tableType === 'assets' ? 280 : 255,
                resizable: true,
                cellRenderer: BalanceSheetTableInstitutionCell,
            },
            {
                field: tableType === 'assets' ? 'serviceModel' : 'type',
                headerName: t('wealthItemType'),
                sortable: true,
                minWidth: 230,
                flex: 1,
                valueFormatter: ({ value, data }: ValueFormatterParams<BalanceSheetTableData, string>) =>
                    tableType === 'assets' ? ((data?.isBankable ? value : data?.type) ?? '') : value ? t(value) : '',
                cellRenderer: TruncatedCellText,
                cellRendererParams: { style: { display: 'block', width: '100%' } },
            },
            ...(tableType === 'assets'
                ? [
                      {
                          field: 'investmentStrategy' as NestedFieldPaths<BalanceSheetTableData>,
                          headerName: t('wealthItemStrategy'),
                          sortable: true,
                          flex: 1,
                          minWidth: 150,
                          valueFormatter: ({ value }: ValueFormatterParams<BalanceSheetTableData, InvestmentStrategy>) =>
                              value ? t(investmentStrategyMap[value]) : '',
                      },
                      {
                          field: 'country' as NestedFieldPaths<BalanceSheetTableData>,
                          headerName: t('wealthCountry'),
                          sortable: true,
                          flex: 1,
                          minWidth: 115,
                          headerClass: 'ag-right-aligned-header',
                          cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
                      },
                  ]
                : [
                      {
                          field: 'refinancingDetails' as NestedFieldPaths<BalanceSheetTableData>,
                          headerName: t('wealthRefinancing'),
                          sortable: false,
                          width: 140,
                          cellRenderer: BalanceSheetTableRefinancingCell,
                      },
                  ]),
            {
                field: 'updatedAt',
                headerName: t('wealthValueAsPer'),
                sortable: true,
                flex: 1,
                minWidth: 140,
                headerClass: 'ag-right-aligned-header',
                cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
                valueFormatter: ({ value }: ValueFormatterParams<BalanceSheetTableData, string>) => (value ? getDateString(value) : ''),
            },
            {
                field: 'currency',
                headerName: t('wealthItemCurrency'),
                sortable: true,
                flex: 1,
                minWidth: 120,
                headerClass: 'ag-right-aligned-header',
                cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
            },
            {
                field: 'amount',
                headerName: t('wealthAmount'),
                sortable: true,
                flex: 1,
                minWidth: 150,
                headerClass: 'ag-right-aligned-header',
                cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
                valueFormatter: amountFormatter,
            },
            {
                field: 'referenceAmount',
                headerName: `${t('wealthAmountIn')} ${referenceCurrency}`,
                sortable: true,
                flex: 1,
                minWidth: 165,
                headerClass: 'ag-right-aligned-header',
                cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
                valueFormatter: amountFormatter,
            },
            {
                headerName: t('sessionAction'),
                sortable: false,
                headerClass: 'ag-right-aligned-header',
                cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
                width: 105,
                cellRenderer: BalanceSheetTableActionCell,
            },
        ];
    }

    return [
        {
            field: 'name',
            headerName: t('wealthItemName'),
            sortable: true,
            resizable: true,
            width: 300,
        },
        {
            field: 'fromDate',
            headerName: t('wealthFromDate'),
            sortable: true,
            flex: 1,
            minWidth: 100,
            headerClass: 'ag-right-aligned-header',
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
            valueFormatter: ({ value }: ValueFormatterParams<BalanceSheetTableData, string>) => getDateString(value ?? '', INCOME_EXPENSE_INPUT_DATE_FORMAT),
        },
        {
            field: 'toDate',
            headerName: t('wealthToDate'),
            sortable: true,
            flex: 1,
            minWidth: 100,
            headerClass: 'ag-right-aligned-header',
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
            valueFormatter: ({ value }: ValueFormatterParams<BalanceSheetTableData, string>) => getDateString(value ?? '', INCOME_EXPENSE_INPUT_DATE_FORMAT),
        },
        {
            field: 'currency',
            headerName: t('wealthItemCurrency'),
            sortable: true,
            flex: 1,
            minWidth: 120,
            headerClass: 'ag-right-aligned-header',
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
        },
        ...(showAll
            ? [
                  {
                      field: 'amount' as NestedFieldPaths<BalanceSheetTableData>,
                      headerName: t('wealthAmountPerYear'),
                      sortable: true,
                      flex: 1,
                      minWidth: 170,
                      headerClass: 'ag-right-aligned-header',
                      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
                      valueFormatter: amountFormatter,
                  },
                  {
                      field: 'referenceAmount' as NestedFieldPaths<BalanceSheetTableData>,
                      headerName: `${t('wealthAmountPerYearIn')} ${referenceCurrency}`,
                      sortable: true,
                      flex: 1,
                      minWidth: 215,
                      headerClass: 'ag-right-aligned-header',
                      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
                      valueFormatter: amountFormatter,
                  },
              ]
            : []),
        {
            field: 'amountCurrentYear',
            headerName: t('wealthAmountCurrentYear'),
            sortable: true,
            flex: 1,
            minWidth: 225,
            headerClass: 'ag-right-aligned-header',
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
            valueFormatter: amountFormatter,
        },
        {
            field: 'referenceAmountCurrentYear',
            headerName: `${t('wealthAmountCurrentYearIn')} ${referenceCurrency}`,
            sortable: true,
            flex: 1,
            minWidth: 280,
            headerClass: 'ag-right-aligned-header',
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
            valueFormatter: amountFormatter,
        },
        {
            headerName: t('sessionAction'),
            sortable: false,
            headerClass: 'ag-right-aligned-header',
            cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'end', textAlign: 'right' },
            width: 105,
            cellRenderer: BalanceSheetTableActionCell,
        },
    ];
}
